import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { localDate } from '../utils/localDate';
import api from '../services/api';
import Navigation from '../components/Navigation';
import Logo from '../components/Logo';
import RiskAlertBanner from '../components/RiskAlertBanner';
import MachineCalculator from './MachineCalculator';
import LinesMachines from './LinesMachines';
import LineSetup from './LineSetup';
import ProductionPlan from './ProductionPlan';
import { ListFooter } from '../ui';

const DAYS = ['MON','TUE','WED','THU','FRI','SAT'];
const LINES_7 = [1,2,3,4,5,6,7];
const LINES_2 = [1,2];
const FINISHING_DEPTS = ['kaj_button','barteck','heat_transfer','pressing','checking','packing'];
const DEPT_LABELS = { kaj_button:'Kaj Button', barteck:'Barteck', heat_transfer:'Heat Transfer', pressing:'Pressing', checking:'Checking', packing:'Packing' };
const NET_MINS = 510;

const getWeekStart = (offset = 0) => {
  const d = new Date();
  d.setDate(d.getDate() - d.getDay() + 1 + offset * 7);
  return d.toISOString().split('T')[0];
};
const getWeekDates = (weekStart) => {
  const dates = [];
  for (let i = 0; i < 6; i++) {
    const d = new Date(weekStart);
    d.setDate(d.getDate() + i);
    dates.push(d.toISOString().split('T')[0]);
  }
  return dates;
};
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('en-IN', { day:'2-digit', month:'short' }) : '';
const mhColor = (mh) => !mh ? '#888' : mh < 30 ? '#22c55e' : mh < 50 ? '#f59e0b' : '#ef4444';
const effColor = (eff) => !eff ? '#888' : eff >= 100 ? '#22c55e' : eff >= 85 ? '#f59e0b' : '#ef4444';

export function PPCContent() {
  const [tab, setTab] = useState('cutting');
  const [weekOffset, setWeekOffset] = useState(0);
  const [weekStart, setWeekStart] = useState(getWeekStart(0));
  const [orders, setOrders] = useState([]);
  const [cuttingData, setCuttingData] = useState([]);
  const [stitchingData, setStitchingData] = useState([]);
  const [finishingData, setFinishingData] = useState([]);
  const [manhourData, setManhourData] = useState({ cutting:[], stitching:[], finishing:[] });
  const [showPlanModal, setShowPlanModal] = useState(false);
  const [showActualModal, setShowActualModal] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [planForm, setPlanForm] = useState({});
  const [actualForm, setActualForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [gpoMH, setGpoMH] = React.useState([]);
  const [officeAttendance, setOfficeAttendance] = React.useState([]);
  const [monthly, setMonthly] = React.useState({ cutting:[], weeks:[] });
  const [quarterly, setQuarterly] = React.useState({ buyer_wise:[], capacity_wise:[], monthly_summary:[] });
  const [month, setMonth] = React.useState(new Date().getMonth()+1);
  const [year, setYear] = React.useState(new Date().getFullYear());
  const [quarter, setQuarter] = React.useState(Math.ceil((new Date().getMonth()+1)/3));
  const [showOfficeModal, setShowOfficeModal] = React.useState(false);
  const [officeForm, setOfficeForm] = React.useState({ dept:'Merchandising', staff_count:'', overtime_mins:0 });
  const MONTHS_LIST = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
  const [loadingPlan, setLoadingPlan] = React.useState([]);
  const [smartSuggestion, setSmartSuggestion] = React.useState(null);
  const [selectedOrderId, setSelectedOrderId] = React.useState('');
  const [loadingSuggestion, setLoadingSuggestion] = React.useState(false);
  const [accepting, setAccepting] = React.useState(false);
  const [tatDates, setTatDates] = React.useState({});
  // Sample Bulletin T&M study linked to the selected GPO (drives the banner + plan).
  const [tmBulletin, setTmBulletin] = React.useState(undefined); // undefined=none selected, null=not found, obj=found
  const [tmPlan, setTmPlan] = React.useState({ sam:'', efficiency:'', order_qty:'' });
  const [tmMachines, setTmMachines] = React.useState({});


  const tabs = [
    { key:'cutting', label:'✂️ Cutting Plan' },
    { key:'stitching', label:'🪡 Stitching Plan' },
    { key:'finishing', label:'👕 Finishing Plan' },
    { key:'manhour', label:'⏱️ Manhour Summary' },
    { key:'manhour_gpo', label:'📦 MH by GPO' },
    { key:'office_mh', label:'🏢 Office MH' },
    { key:'monthly', label:'📅 Monthly Plan' },
    { key:'quarterly', label:'📊 Quarterly Plan' },
    { key:'loading', label:'📋 Loading Plan' },
    { key:'machine_calc', label:'🧮 Machine Calculator' },
    { key:'lines_machines', label:'🏭 Lines & Machines' },
    { key:'line_setup', label:'🧵 Line Setup' },
    { key:'production_plan', label:'📋 Production Plan' },
  ];

  useEffect(() => {
    const ws = getWeekStart(weekOffset);
    setWeekStart(ws);
    fetchData(ws);
    api.get('/orders').then(r => setOrders(r.data.data || [])).catch(() => {});
  }, [weekOffset]);

  const fetchData = async (ws) => {
    try {
      const [c, s, f, m] = await Promise.all([
        api.get('/planning/cutting?week_start=' + ws),
        api.get('/planning/stitching?week_start=' + ws),
        api.get('/planning/finishing?week_start=' + ws),
        api.get('/planning/manhour-summary?week_start=' + ws),
      ]);
      setCuttingData(c.data.data || []);
      setStitchingData(s.data.data || []);
      setFinishingData(f.data.data || []);
      setManhourData(m.data || { cutting:[], stitching:[], finishing:[] });
    } catch(err) { console.error(err); }
  };

  const getCell = (data, line, date, subDept) =>
    data.find(d => d.line_no === line && d.plan_date && d.plan_date.startsWith(date) && (!subDept || d.sub_dept === subDept));

  const openPlan = (line, date, subDept) => {
    setSelectedCell({ line, date, subDept });
    setPlanForm({ line_no:line, plan_date:date, sub_dept:subDept, week_start:weekStart });
    setShowPlanModal(true);
  };

  const openActual = (cell) => {
    setSelectedCell(cell);
    setActualForm({ actual_qty:cell.actual_qty||'', operator_count:cell.operator_count||'', helper_count:cell.helper_count||'', overtime_mins:cell.overtime_mins||'', sam:cell.sam||'', rejected_qty:cell.rejected_qty||'', rework_qty:cell.rework_qty||'', remarks:cell.remarks||'' });
    setShowActualModal(true);
  };

  const savePlan = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.post('/planning/' + tab, planForm); setShowPlanModal(false); fetchData(weekStart); }
    catch(err) { alert('Error: ' + err.message); }
    finally { setSaving(false); }
  };

  const saveActual = async (e) => {
    e.preventDefault(); setSaving(true);
    try { await api.patch('/planning/' + tab + '/' + selectedCell.id + '/actual', actualForm); setShowActualModal(false); fetchData(weekStart); }
    catch(err) { alert('Error: ' + err.message); }
    finally { setSaving(false); }
  };

  const calcMH = (ops, helpers, actual, ot) => {
    if (!actual || Number(actual) === 0) return null;
    return Math.round(((Number(ops)||0) + (Number(helpers)||0)) * (NET_MINS + (Number(ot)||0)) / Number(actual));
  };

  const calcEff = (actual, target) => {
    if (!target || Number(target) === 0) return null;
    return Math.round((Number(actual) / Number(target)) * 100);
  };


  useEffect(() => {
    if(tab==='manhour_gpo') api.get('/planning/manhour-gpo?week_start='+weekStart).then(r=>setGpoMH(r.data.data||[])).catch(()=>{});
    if(tab==='loading') api.get('/planning/loading-plan').then(r=>setLoadingPlan(r.data.data||[])).catch(()=>{});
    if(tab==='office_mh') api.get('/planning/office-attendance').then(r=>setOfficeAttendance(r.data.data||[])).catch(()=>{});
    if(tab==='monthly') api.get('/display/monthly?month='+month+'&year='+year).then(r=>setMonthly(r.data||{cutting:[],weeks:[]})).catch(()=>{});
    if(tab==='quarterly') api.get('/display/quarterly?quarter='+quarter+'&year='+year).then(r=>setQuarterly(r.data||{buyer_wise:[],capacity_wise:[],monthly_summary:[]})).catch(()=>{});
  }, [tab, month, year, quarter, weekStart]);
  const fetchSmartSuggestion = async (order_id) => {
    setLoadingSuggestion(true); setSmartSuggestion(null);
    try {
      const r = await api.get('/smart-loading/suggest/'+order_id);
      setSmartSuggestion(r.data);
    } catch(e) { alert('Error: '+e.message); }
    finally { setLoadingSuggestion(false); }
  };

  // Pull the GPO's linked Sample Bulletin T&M study and auto-populate the plan.
  const fetchTmByGpo = async (gpo_no) => {
    if (!gpo_no) { setTmBulletin(undefined); return; }
    try {
      const r = await api.get('/sample-bulletins/by-gpo/' + encodeURIComponent(gpo_no));
      const data = r.data?.data || null;
      setTmBulletin(data);
      if (data) {
        const tm = data.tm || {};
        setTmPlan({
          sam: tm.total_sam != null ? tm.total_sam : '',
          efficiency: tm.avg_efficiency != null ? Math.round(tm.avg_efficiency) : '',
          order_qty: tm.order_qty != null ? tm.order_qty : (data.order_qty || ''),
        });
        setTmMachines({ ...(tm.machine_counts || {}) });
      }
    } catch (e) { setTmBulletin(null); }
  };

  const acceptSuggestion = async (line_no) => {
    if (!smartSuggestion) return;
    setAccepting(true);
    try {
      const { requirements, order } = smartSuggestion;
      const r = await api.post('/smart-loading/accept/'+order.id, {
        line_no,
        line_in_date: requirements.line_in_date,
        line_out_date: requirements.line_out_date,
        daily_target: requirements.daily_target,
        gpo_no: order.gpo_no,
        style: order.style || order.garment_type,
        buyer: order.buyer_name,
        order_qty: requirements.order_qty
      });
      alert(r.data.message);
      fetchData(weekStart);
      setTab('stitching');
    } catch(e) { alert('Error: '+e.message); }
    finally { setAccepting(false); }
  };

  const weekDates = getWeekDates(weekStart);
  const inputStyle = { width:'100%', padding:'7px 10px', border:'1px solid #ddd', borderRadius:6, fontSize:13, boxSizing:'border-box' };
  const labelStyle = { fontSize:11, color:'#888', fontWeight:600, display:'block', marginBottom:4, textTransform:'uppercase' };

  const CellBox = ({ cell, line, date, subDept }) => {
    const hasData = cell && (cell.target_qty > 0 || cell.actual_qty > 0);
    const eff = hasData ? calcEff(cell.actual_qty, cell.target_qty) : null;
    const mh = hasData ? calcMH(cell.operator_count, cell.helper_count, cell.actual_qty, cell.overtime_mins) : null;
    return (
      <td style={{ padding:4, border:'1px solid #e8e8e8', minWidth:110, verticalAlign:'top' }}>
        {!hasData ? (
          <div onClick={() => openPlan(line, date, subDept)} style={{ height:60, display:'flex', alignItems:'center', justifyContent:'center', cursor:'pointer', borderRadius:4, border:'1px dashed #ddd', color:'#bbb', fontSize:11 }}>+ Plan</div>
        ) : (
          <div onClick={() => openActual(cell)} style={{ background:cell.actual_qty>0?'#f0fdf4':'#fffbeb', borderRadius:4, padding:4, cursor:'pointer', border:'1px solid ' + (cell.actual_qty>0?'#bbf7d0':'#fde68a') }}>
            <div style={{ fontSize:10, color:'#888', marginBottom:2 }}>{cell.gpo_no} · {cell.style}</div>
            <div style={{ display:'flex', justifyContent:'space-between', marginBottom:2 }}>
              <span style={{ fontSize:11 }}>T: <b>{Number(cell.target_qty).toLocaleString('en-IN')}</b></span>
              <span style={{ fontSize:11, color:cell.actual_qty>0?'#166534':'#666' }}>A: <b>{cell.actual_qty>0?Number(cell.actual_qty).toLocaleString('en-IN'):'—'}</b></span>
            </div>
            {eff !== null && <div style={{ display:'flex', justifyContent:'space-between' }}>
              <span style={{ fontSize:10, color:effColor(eff), fontWeight:700 }}>{eff}%</span>
              {mh && <span style={{ fontSize:10, color:mhColor(mh), fontWeight:700 }}>{mh}m/pc</span>}
            </div>}
            {cell.operator_count>0 && <div style={{ fontSize:9, color:'#aaa' }}>👤 {cell.operator_count}{cell.helper_count?'+'+cell.helper_count+'h':''}</div>}
          </div>
        )}
      </td>
    );
  };

  const PlanGrid = ({ data, lines, subDepts }) => (
    <div style={{ overflowX:'auto' }}>
      <table style={{ borderCollapse:'collapse', width:'100%', fontSize:12 }}>
        <thead>
          <tr style={{ background:'#3B3A8F' }}>
            <th style={{ padding:'8px 12px', color:'white', textAlign:'left', minWidth:70 }}>Line</th>
            {subDepts && <th style={{ padding:'8px 12px', color:'white', textAlign:'left', minWidth:120 }}>Dept</th>}
            {weekDates.map((d,i) => (
              <th key={d} style={{ padding:'8px 12px', color:'white', textAlign:'center', minWidth:115 }}>
                {DAYS[i]}<br/><span style={{ fontSize:10, opacity:0.8 }}>{fmtDate(d)}</span>
              </th>
            ))}
            <th style={{ padding:'8px 12px', color:'white', textAlign:'center', minWidth:90 }}>Week Total</th>
          </tr>
        </thead>
        <tbody>
          {lines.map(line =>
            subDepts ? FINISHING_DEPTS.map((dept,di) => {
              const wt = weekDates.reduce((s,d)=>s+(getCell(data,line,d,dept)?.target_qty||0),0);
              const wa = weekDates.reduce((s,d)=>s+(getCell(data,line,d,dept)?.actual_qty||0),0);
              const we = calcEff(wa,wt);
              return (
                <tr key={line+'-'+dept} style={{ borderBottom:'1px solid #f0f0f0' }}>
                  {di===0 && <td rowSpan={FINISHING_DEPTS.length} style={{ padding:'8px 12px', fontWeight:700, color:'#3B3A8F', verticalAlign:'middle', background:'#EEEDF8', textAlign:'center', border:'1px solid #ddd' }}>Line {line}</td>}
                  <td style={{ padding:'6px 10px', fontSize:12, fontWeight:600, color:'#555', background:'#fafafa', border:'1px solid #eee' }}>{DEPT_LABELS[dept]}</td>
                  {weekDates.map(d => <CellBox key={d} cell={getCell(data,line,d,dept)} line={line} date={d} subDept={dept} />)}
                  <td style={{ padding:'8px 10px', textAlign:'center', background:'#F7F6F3' }}>
                    <div style={{ fontSize:12, fontWeight:700 }}>{wt.toLocaleString('en-IN')}</div>
                    <div style={{ fontSize:12, color:'#166534', fontWeight:700 }}>{wa.toLocaleString('en-IN')}</div>
                    {we && <div style={{ fontSize:11, color:effColor(we) }}>{we}%</div>}
                  </td>
                </tr>
              );
            }) : (
              <tr key={line} style={{ borderBottom:'1px solid #f0f0f0' }}>
                <td style={{ padding:'8px 12px', fontWeight:700, color:'#3B3A8F', background:'#EEEDF8', textAlign:'center', border:'1px solid #ddd' }}>Line {line}</td>
                {weekDates.map(d => <CellBox key={d} cell={getCell(data,line,d,null)} line={line} date={d} subDept={null} />)}
                <td style={{ padding:'8px 10px', textAlign:'center', background:'#F7F6F3' }}>
                  {(()=>{ const wt=weekDates.reduce((s,d)=>s+(getCell(data,line,d,null)?.target_qty||0),0); const wa=weekDates.reduce((s,d)=>s+(getCell(data,line,d,null)?.actual_qty||0),0); const we=calcEff(wa,wt); return React.createElement(React.Fragment,null,React.createElement('div',{style:{fontSize:12,fontWeight:700}},wt.toLocaleString('en-IN')),React.createElement('div',{style:{fontSize:12,color:'#166534',fontWeight:700}},wa.toLocaleString('en-IN')),we&&React.createElement('div',{style:{fontSize:11,color:effColor(we)}},we+'%')); })()}
                </td>
              </tr>
            )
          )}
          <tr style={{ background:'#f0f2f8', borderTop:'2px solid #3B3A8F' }}>
            <td colSpan={subDepts?2:1} style={{ padding:'8px 12px', fontWeight:700 }}>DAY TOTAL</td>
            {weekDates.map(d => {
              const t = lines.reduce((s,l)=>s+(subDepts?FINISHING_DEPTS.reduce((ss,dept)=>ss+(getCell(data,l,d,dept)?.target_qty||0),0):(getCell(data,l,d,null)?.target_qty||0)),0);
              const a = lines.reduce((s,l)=>s+(subDepts?FINISHING_DEPTS.reduce((ss,dept)=>ss+(getCell(data,l,d,dept)?.actual_qty||0),0):(getCell(data,l,d,null)?.actual_qty||0)),0);
              const e = calcEff(a,t);
              return <td key={d} style={{ padding:'8px 10px', textAlign:'center' }}>
                <div style={{ fontSize:12, fontWeight:700 }}>{t.toLocaleString('en-IN')}</div>
                <div style={{ fontSize:12, color:'#166534', fontWeight:700 }}>{a.toLocaleString('en-IN')}</div>
                {e && <div style={{ fontSize:11, color:effColor(e), fontWeight:700 }}>{e}%</div>}
              </td>;
            })}
            <td style={{ padding:'8px 10px', textAlign:'center', background:'#EEEDF8', fontWeight:700 }}>
              {(()=>{ const gt=lines.reduce((s,l)=>s+weekDates.reduce((ss,d)=>ss+(subDepts?FINISHING_DEPTS.reduce((sss,dept)=>sss+(getCell(data,l,d,dept)?.target_qty||0),0):(getCell(data,l,d,null)?.target_qty||0)),0),0); const ga=lines.reduce((s,l)=>s+weekDates.reduce((ss,d)=>ss+(subDepts?FINISHING_DEPTS.reduce((sss,dept)=>sss+(getCell(data,l,d,dept)?.actual_qty||0),0):(getCell(data,l,d,null)?.actual_qty||0)),0),0); const ge=calcEff(ga,gt); return React.createElement(React.Fragment,null,React.createElement('div',{style:{fontSize:13}},gt.toLocaleString('en-IN')),React.createElement('div',{style:{fontSize:13,color:'#166534'}},ga.toLocaleString('en-IN')),ge&&React.createElement('div',{style:{fontSize:12,color:effColor(ge)}},ge+'%')); })()}
            </td>
          </tr>
        </tbody>
      </table>
    </div>
  );

  return (
    <>
      <div style={{ padding:24 }}>
        <div style={{ background:'white', borderRadius:10, boxShadow:'none',border:'1px solid #E8E7E7' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'0 20px', borderBottom:'1px solid #eee' }}>
            <div style={{ display:'flex' }}>
              {tabs.map(t => (
                <button key={t.key} onClick={() => setTab(t.key)} style={{ padding:'14px 18px', border:'none', background:'transparent', cursor:'pointer', fontSize:13, fontWeight:tab===t.key?600:400, color:tab===t.key?'#3B3A8F':'#888', borderBottom:tab===t.key?'2px solid #3B3A8F':'2px solid transparent' }}>{t.label}</button>
              ))}
            </div>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <button onClick={() => setWeekOffset(w=>w-1)} style={{ padding:'5px 12px', background:'#f0f2f8', border:'1px solid #ddd', borderRadius:5, cursor:'pointer' }}>← Prev</button>
              <span style={{ fontSize:13, fontWeight:600, color:'#3B3A8F' }}>Week: {fmtDate(weekStart)} — {fmtDate(weekDates[5])}</span>
              <button onClick={() => setWeekOffset(w=>w+1)} style={{ padding:'5px 12px', background:'#f0f2f8', border:'1px solid #ddd', borderRadius:5, cursor:'pointer' }}>Next →</button>
              {weekOffset!==0 && <button onClick={() => setWeekOffset(0)} style={{ padding:'5px 12px', background:'#3B3A8F', color:'white', border:'none', borderRadius:5, cursor:'pointer', fontSize:12 }}>This Week</button>}
            </div>
          </div>
          <div style={{ padding:20 }}>
            {/* Open capacity/delivery risks flagged in Risk Analysis (Build 2). */}
            <RiskAlertBanner doc="PPC" />
            {/* T&M Study link banner — driven by the selected GPO's Sample Bulletin */}
            {tmBulletin && (
              <div style={{ background:'#dcfce7', border:'1px solid #86efac', color:'#166534', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:13, fontWeight:600 }}>
                📋 Linked: {tmBulletin.bulletin_no} | SAM: {Number(tmBulletin.tm?.total_sam||0).toFixed(2)} mins | Efficiency: {tmBulletin.tm?.avg_efficiency!=null?Number(tmBulletin.tm.avg_efficiency).toFixed(0):'—'}% | Bottleneck: {tmBulletin.tm?.bottleneck_name||'—'} ({Number(tmBulletin.tm?.bottleneck_sam||0).toFixed(2)} mins) | IE: {tmBulletin.tm?.ie_officer||'—'}
              </div>
            )}
            {tmBulletin===null && (
              <div style={{ background:'#FEF3C7', border:'1px solid #FDE68A', color:'#92400E', borderRadius:8, padding:'10px 14px', marginBottom:16, fontSize:13, fontWeight:600 }}>
                ⚠️ No T&M Study found for this GPO. Create a Sample Bulletin first.
              </div>
            )}
            {tab==='machine_calc' && <MachineCalculator />}
            {tab==='lines_machines' && <LinesMachines />}
            {tab==='line_setup' && <LineSetup />}
            {tab==='production_plan' && <ProductionPlan />}
            {tab==='cutting' && <PlanGrid data={cuttingData} lines={LINES_7} subDepts={false} />}
            {tab==='stitching' && <PlanGrid data={stitchingData} lines={LINES_7} subDepts={false} />}
            {tab==='finishing' && <PlanGrid data={finishingData} lines={LINES_2} subDepts={true} />}
            {tab==='manhour' && (
              <div>
                <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:16, marginBottom:24 }}>
                  {['cutting','stitching','finishing'].map(dept => {
                    const rows = (manhourData[dept]||[]).filter(r=>r.mh_per_garment_mins>0);
                    const avg = rows.length ? Math.round(rows.reduce((s,r)=>s+(r.mh_per_garment_mins||0),0)/rows.length) : null;
                    return (
                      <div key={dept} style={{ background:'white', border:'1px solid #eee', borderRadius:8, padding:16 }}>
                        <div style={{ fontSize:11, fontWeight:700, color:'#3B3A8F', textTransform:'uppercase', marginBottom:8 }}>{dept} Avg MH/Garment</div>
                        <div style={{ fontSize:32, fontWeight:700, color:avg?mhColor(avg):'#aaa' }}>{avg?avg+'m':'—'}</div>
                        <div style={{ fontSize:11, color:'#888' }}>mins per garment this week</div>
                        <div style={{ fontSize:10, color:'#bbb', marginTop:4 }}>🟢 &lt;30m Good · 🟡 30-50m Ok · 🔴 &gt;50m High</div>
                      </div>
                    );
                  })}
                </div>
                {['cutting','stitching','finishing'].map(dept => {
                  const rows = manhourData[dept]||[];
                  if (!rows.length) return <div key={dept} style={{ color:'#aaa', fontSize:13, marginBottom:16, padding:'20px', textAlign:'center', border:'1px dashed #eee', borderRadius:8 }}>No {dept} actuals recorded this week</div>;
                  return (
                    <div key={dept} style={{ marginBottom:24 }}>
                      <div style={{ fontWeight:700, fontSize:14, color:'#333', marginBottom:10, textTransform:'capitalize', padding:'8px 0', borderBottom:'2px solid #3B3A8F' }}>{dept} Department</div>
                      <table style={{ width:'100%', borderCollapse:'collapse', fontSize:12 }}>
                        <thead><tr style={{ background:'#F7F6F3' }}>
                          {['Date','Line','GPO','Style','Operators','Actual','OT Mins','MH/Garment','Efficiency'].map(h => (
                            <th key={h} style={{ padding:'8px 10px', textAlign:'left', color:'#555', fontWeight:600, borderBottom:'2px solid #eee', fontSize:11 }}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {rows.map((r,i) => {
                            const eff = calcEff(r.actual_qty, r.target_qty);
                            return (
                              <tr key={i} style={{ borderBottom:'1px solid #f5f5f5' }}>
                                <td style={{ padding:'8px 10px' }}>{fmtDate(r.plan_date)}</td>
                                <td style={{ padding:'8px 10px' }}>Line {r.line_no}</td>
                                <td style={{ padding:'8px 10px', fontFamily:'monospace', fontWeight:600, color:'#3B3A8F' }}>{r.gpo_no}</td>
                                <td style={{ padding:'8px 10px' }}>{r.style||'—'}</td>
                                <td style={{ padding:'8px 10px' }}>{r.operator_count||'—'}</td>
                                <td style={{ padding:'8px 10px', fontWeight:600 }}>{Number(r.actual_qty).toLocaleString('en-IN')}</td>
                                <td style={{ padding:'8px 10px' }}>{r.overtime_mins||0}</td>
                                <td style={{ padding:'8px 10px' }}>
                                  <span style={{ fontWeight:700, color:mhColor(r.mh_per_garment_mins), fontSize:13 }}>{r.mh_per_garment_mins?r.mh_per_garment_mins+'m':'—'}</span>
                                </td>
                                <td style={{ padding:'8px 10px' }}>
                                  {eff && <span style={{ padding:'2px 8px', borderRadius:10, fontSize:11, fontWeight:600, background:eff>=100?'#dcfce7':eff>=85?'#fef9c3':'#fee2e2', color:effColor(eff) }}>{eff}%</span>}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>



            {tab==='loading' && (
              <div>
                <div style={{background:'#EEEDF8',borderRadius:8,padding:16,marginBottom:20}}>
                  <div style={{fontWeight:700,fontSize:14,color:'#3B3A8F',marginBottom:10}}>🤖 Smart Line Loading</div>
                  <div style={{display:'flex',gap:12,alignItems:'center',marginBottom:12}}>
                    <select value={selectedOrderId} onChange={e=>{
                        const id=e.target.value; setSelectedOrderId(id);
                        const o=orders.find(x=>String(x.id)===String(id));
                        if(id){ fetchSmartSuggestion(id); fetchTmByGpo(o?.gpo_no||o?.order_no); }
                        else { setTmBulletin(undefined); }
                      }}
                      style={{padding:'8px 12px',border:'1px solid #ddd',borderRadius:6,fontSize:13,flex:1}}>
                      <option value="">Select GPO to get loading suggestion...</option>
                      {orders.map(o=><option key={o.id} value={o.id}>{o.gpo_no||o.order_no} — {o.buyer_name} — {o.style||o.garment_type}</option>)}
                    </select>
                    {loadingSuggestion && <span style={{fontSize:13,color:'#3B3A8F'}}>⏳ Analysing...</span>}
                  </div>
                  {/* T&M-based plan — auto-filled from the GPO's Sample Bulletin (editable) */}
                  {tmBulletin && (()=>{
                    const samN=parseFloat(tmPlan.sam)||0, effN=parseFloat(tmPlan.efficiency)||0, qtyN=parseFloat(tmPlan.order_qty)||0;
                    const dailyOutput = samN>0 ? Math.floor((480*(effN/100))/samN) : 0;
                    const daysReq = dailyOutput>0 ? Math.ceil(qtyN/dailyOutput) : 0;
                    return (
                      <div style={{background:'white',border:'1px solid #C7D2FE',borderRadius:8,padding:16,marginBottom:16}}>
                        <div style={{fontSize:12,fontWeight:700,color:'#3730A3',textTransform:'uppercase',letterSpacing:0.5,marginBottom:10}}>📐 T&M-based Production Plan <span style={{fontWeight:400,textTransform:'none',color:'#888'}}>(auto-filled from {tmBulletin.bulletin_no} — editable)</span></div>
                        <div style={{display:'grid',gridTemplateColumns:'repeat(5,1fr)',gap:12,marginBottom:14}}>
                          <div><label style={labelStyle}>SAM (mins)</label><input style={inputStyle} type="number" step="0.001" value={tmPlan.sam} onChange={e=>setTmPlan(p=>({...p,sam:e.target.value}))}/></div>
                          <div><label style={labelStyle}>Target Efficiency %</label><input style={inputStyle} type="number" step="1" value={tmPlan.efficiency} onChange={e=>setTmPlan(p=>({...p,efficiency:e.target.value}))}/></div>
                          <div><label style={labelStyle}>Order Qty</label><input style={inputStyle} type="number" value={tmPlan.order_qty} onChange={e=>setTmPlan(p=>({...p,order_qty:e.target.value}))}/></div>
                          <div><label style={labelStyle}>Daily Output / operator</label><div style={{...inputStyle,background:'#EEEDF8',fontWeight:700,color:'#3B3A8F'}}>{dailyOutput.toLocaleString('en-IN')} pcs</div></div>
                          <div><label style={labelStyle}>Days Required</label><div style={{...inputStyle,background:'#EEEDF8',fontWeight:700,color:'#3B3A8F'}}>{daysReq} days</div></div>
                        </div>
                        {Object.keys(tmMachines).length>0 && (
                          <div>
                            <div style={{fontSize:11,fontWeight:700,color:'#888',textTransform:'uppercase',marginBottom:8}}>Machine Allocation <span style={{fontWeight:400,textTransform:'none'}}>(from T&M operations — override as needed)</span></div>
                            <div style={{display:'flex',gap:10,flexWrap:'wrap'}}>
                              {Object.entries(tmMachines).map(([type,count])=>(
                                <div key={type} style={{display:'flex',alignItems:'center',gap:6,background:'#F7F6F3',borderRadius:6,padding:'6px 10px'}}>
                                  <span style={{fontSize:13,fontWeight:700,color:'#3B3A8F'}}>{type} ×</span>
                                  <input type="number" min="0" value={count} onChange={e=>setTmMachines(m=>({...m,[type]:e.target.value}))} style={{width:56,padding:'4px 6px',border:'1px solid #ddd',borderRadius:5,fontSize:13,textAlign:'center'}}/>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })()}
                  {smartSuggestion && (
                    <div>
                      {(smartSuggestion.warnings||[]).map((w,i)=>(
                        <div key={i} style={{background:'#fff5f5',border:'1px solid #fca5a5',borderRadius:6,padding:'8px 12px',marginBottom:8,fontSize:12,color:'#E22D2D'}}>{w}</div>
                      ))}
                      <div style={{display:'grid',gridTemplateColumns:'repeat(6,1fr)',gap:10,marginBottom:14}}>
                        {[
                          {label:'Order Qty',value:Number(smartSuggestion.requirements.order_qty||0).toLocaleString('en-IN')},
                          {label:'Total SAM',value:smartSuggestion.requirements.total_sam>0?smartSuggestion.requirements.total_sam.toFixed(2)+'m':'N/A'},
                          {label:'TAT Days',value:smartSuggestion.requirements.tat_days?smartSuggestion.requirements.tat_days+'d':'N/A'},
                          {label:'Daily Target',value:smartSuggestion.requirements.daily_target?smartSuggestion.requirements.daily_target+' pcs':'N/A'},
                          {label:'Machines Needed',value:smartSuggestion.requirements.machines_required||'N/A',hi:true},
                          {label:'Output/Machine',value:smartSuggestion.requirements.output_per_machine?smartSuggestion.requirements.output_per_machine+' pcs/day':'N/A'},
                        ].map(card=>(
                          <div key={card.label} style={{background:'white',borderRadius:6,padding:'10px 12px',border:card.hi?'2px solid #E22D2D':'1px solid #ddd'}}>
                            <div style={{fontSize:10,color:'#888',fontWeight:600,textTransform:'uppercase'}}>{card.label}</div>
                            <div style={{fontSize:16,fontWeight:700,color:card.hi?'#E22D2D':'#3B3A8F',marginTop:2}}>{card.value}</div>
                          </div>
                        ))}
                      </div>
                      <div style={{display:'flex',gap:8,marginBottom:14,flexWrap:'wrap'}}>
                        {smartSuggestion.requirements.cutting_date && <div style={{background:'white',borderRadius:6,padding:'6px 12px',fontSize:12,border:'1px solid #ddd'}}>✂️ Cutting: <b>{new Date(smartSuggestion.requirements.cutting_date).toLocaleDateString('en-IN')}</b></div>}
                        {smartSuggestion.requirements.line_in_date && <div style={{background:'white',borderRadius:6,padding:'6px 12px',fontSize:12,border:'1px solid #ddd'}}>🪡 Line In: <b>{new Date(smartSuggestion.requirements.line_in_date).toLocaleDateString('en-IN')}</b></div>}
                        {smartSuggestion.requirements.line_out_date && <div style={{background:'white',borderRadius:6,padding:'6px 12px',fontSize:12,border:'1px solid #ddd'}}>👕 Line Out: <b>{new Date(smartSuggestion.requirements.line_out_date).toLocaleDateString('en-IN')}</b></div>}
                        {smartSuggestion.bulletin && <div style={{background:'#dcfce7',borderRadius:6,padding:'6px 12px',fontSize:12,border:'1px solid #86efac'}}>📋 {smartSuggestion.bulletin.pb_no} · SAM: <b>{smartSuggestion.requirements.total_sam.toFixed(2)}m</b></div>}
                      </div>
                      {smartSuggestion.machine_allocation?.length>0 && (
                        <div style={{marginBottom:14}}>
                          <div style={{fontSize:12,fontWeight:700,marginBottom:8}}>Suggested Machine Allocation:</div>
                          <div style={{display:'flex',gap:8,flexWrap:'wrap'}}>
                            {smartSuggestion.machine_allocation.map((m,i)=>(
                              <div key={i} style={{background:m.shortage>0?'#fff5f5':'#f0fdf4',border:'1px solid '+(m.shortage>0?'#fca5a5':'#86efac'),borderRadius:6,padding:'8px 12px',fontSize:12}}>
                                <div style={{fontWeight:600}}>{m.machine_name}</div>
                                <div style={{color:'#3B3A8F',fontWeight:700}}>{m.needed} needed</div>
                                <div style={{color:'#888'}}>Available: {m.available_qty}</div>
                                {m.shortage>0&&<div style={{color:'#ef4444',fontWeight:600}}>⚠️ Short by {m.shortage}</div>}
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                      <div style={{fontSize:12,fontWeight:700,marginBottom:8}}>Line Recommendations:</div>
                      <div style={{display:'grid',gridTemplateColumns:'repeat(7,1fr)',gap:8}}>
                        {smartSuggestion.line_suggestions.map((line,i)=>(
                          <div key={i} style={{border:'2px solid '+(line.recommended?'#3B3A8F':line.is_occupied?'#fca5a5':'#e5e7eb'),borderRadius:8,padding:'10px 8px',textAlign:'center',background:line.recommended?'#EEEDF8':line.is_occupied?'#fff5f5':'white'}}>
                            <div style={{fontSize:14,fontWeight:900,color:line.recommended?'#3B3A8F':'#888'}}>Line {line.line_no}</div>
                            <div style={{fontSize:10,marginTop:4,color:line.is_occupied?'#ef4444':'#22c55e',fontWeight:600}}>{line.is_occupied?line.active_gpos+' GPO loaded':'Free'}</div>
                            <div style={{fontSize:10,color:'#888',marginTop:2}}>{new Date(line.available_from).toLocaleDateString('en-IN',{day:'2-digit',month:'short'})}</div>
                            {line.ot_needed_mins>0&&<div style={{fontSize:9,color:'#f59e0b',marginTop:2}}>OT: {line.ot_needed_mins}m</div>}
                            {line.recommended&&smartSuggestion.requirements.line_in_date&&(
                              <button onClick={()=>acceptSuggestion(line.line_no)} disabled={accepting}
                                style={{marginTop:8,padding:'4px 8px',background:'#3B3A8F',color:'white',border:'none',borderRadius:4,fontSize:10,cursor:'pointer',fontWeight:600,width:'100%'}}>
                                {accepting?'...':'✓ Load'}
                              </button>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
                <div style={{marginBottom:12,fontSize:13,color:'#666'}}>T&A planned dates vs PPC actual planning</div>
                {loadingPlan.length===0 ? (
                  <div style={{padding:30,textAlign:'center',color:'#aaa',border:'1px dashed #eee',borderRadius:8}}>No orders found</div>
                ) : (
                  <div style={{overflowX:'auto'}}>
                    <table style={{width:'100%',borderCollapse:'collapse',fontSize:11}}>
                      <thead>
                        <tr style={{background:'#3B3A8F'}}>
                          {['GPO','Buyer','Style','Order Qty','Delivery','T&A Cutting','T&A Line In','T&A Line Out','T&A Dispatch','PPC Cut Start','PPC Cut Actual','PPC Stitch Start','Status'].map(h=>(
                            <th key={h} style={{padding:'8px 10px',color:'white',textAlign:'left',fontSize:10,whiteSpace:'nowrap'}}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {loadingPlan.map((r,i)=>{
                          const today=new Date();
                          const taCut=r.ta_cutting_start?new Date(r.ta_cutting_start):null;
                          const ppcCut=r.ppc_cutting_start?new Date(r.ppc_cutting_start):null;
                          const isLate=taCut&&ppcCut&&ppcCut>taCut;
                          const isNotPlanned=taCut&&!ppcCut&&taCut<today;
                          return (
                            <tr key={i} style={{borderBottom:'1px solid #f0f0f0',background:isLate?'#fff5f5':isNotPlanned?'#fffbeb':'white'}}>
                              <td style={{padding:'8px 10px',fontFamily:'monospace',fontWeight:700,color:'#3B3A8F'}}>{r.gpo_no}</td>
                              <td style={{padding:'8px 10px',whiteSpace:'nowrap'}}>{r.buyer}</td>
                              <td style={{padding:'8px 10px'}}>{r.style||'-'}</td>
                              <td style={{padding:'8px 10px',textAlign:'right'}}>{Number(r.order_qty||0).toLocaleString('en-IN')}</td>
                              <td style={{padding:'8px 10px',whiteSpace:'nowrap',color:r.delivery_date&&new Date(r.delivery_date)<today?'#ef4444':'#333'}}>{r.delivery_date?new Date(r.delivery_date).toLocaleDateString('en-IN'):'-'}</td>
                              <td style={{padding:'8px 10px',whiteSpace:'nowrap',color:'#3B3A8F',fontWeight:600}}>{r.ta_cutting_start?new Date(r.ta_cutting_start).toLocaleDateString('en-IN'):'-'}</td>
                              <td style={{padding:'8px 10px',whiteSpace:'nowrap'}}>{r.ta_line_in?new Date(r.ta_line_in).toLocaleDateString('en-IN'):'-'}</td>
                              <td style={{padding:'8px 10px',whiteSpace:'nowrap'}}>{r.ta_line_out?new Date(r.ta_line_out).toLocaleDateString('en-IN'):'-'}</td>
                              <td style={{padding:'8px 10px',whiteSpace:'nowrap'}}>{r.ta_dispatch?new Date(r.ta_dispatch).toLocaleDateString('en-IN'):'-'}</td>
                              <td style={{padding:'8px 10px',whiteSpace:'nowrap',color:isLate?'#ef4444':'#166534',fontWeight:600}}>{r.ppc_cutting_start?new Date(r.ppc_cutting_start).toLocaleDateString('en-IN'):<span style={{color:'#f59e0b'}}>Not Planned</span>}</td>
                              <td style={{padding:'8px 10px',textAlign:'right',color:'#166534',fontWeight:600}}>{r.ppc_cutting_actual>0?Number(r.ppc_cutting_actual).toLocaleString('en-IN'):'-'}</td>
                              <td style={{padding:'8px 10px',whiteSpace:'nowrap'}}>{r.ppc_stitching_start?new Date(r.ppc_stitching_start).toLocaleDateString('en-IN'):<span style={{color:'#f59e0b'}}>Not Planned</span>}</td>
                              <td style={{padding:'8px 10px'}}>
                                {isLate&&<span style={{padding:'2px 8px',borderRadius:10,background:'#fee2e2',color:'#ef4444',fontSize:10,fontWeight:600}}>⚠️ Late</span>}
                                {isNotPlanned&&<span style={{padding:'2px 8px',borderRadius:10,background:'#fef9c3',color:'#9A6B00',fontSize:10,fontWeight:600}}>📋 Unplanned</span>}
                                {!isLate&&!isNotPlanned&&ppcCut&&<span style={{padding:'2px 8px',borderRadius:10,background:'#dcfce7',color:'#166534',fontSize:10,fontWeight:600}}>✅ On Track</span>}
                                {!taCut&&<span style={{padding:'2px 8px',borderRadius:10,background:'#f3f4f6',color:'#888',fontSize:10}}>No T&A</span>}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                    <ListFooter shown={loadingPlan.length} total={loadingPlan.length} noun="orders" />
                  </div>
                )}
              </div>
            )}
            {tab==='manhour_gpo' && (
              <div>
                <div style={{marginBottom:12,fontSize:13,color:'#666'}}>Combined manhour per garment across Cutting + Stitching + Finishing by GPO</div>
                {gpoMH.length===0 ? (
                  <div style={{padding:40,textAlign:'center',color:'#aaa',border:'1px dashed #eee',borderRadius:8}}>No GPO manhour data - enter actuals in Cutting/Stitching/Finishing tabs first</div>
                ) : (
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                    <thead><tr style={{background:'#F7F6F3'}}>
                      {['GPO','Style','Buyer','Cutting MH','Stitching MH','Finishing MH','Total MH/Garment'].map(h=>(
                        <th key={h} style={{padding:'8px 10px',textAlign:'left',color:'#555',fontWeight:600,borderBottom:'2px solid #eee',fontSize:11}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {gpoMH.map((r,i)=>(
                        <tr key={i} style={{borderBottom:'1px solid #f5f5f5'}}>
                          <td style={{padding:'8px 10px',fontFamily:'monospace',fontWeight:600,color:'#3B3A8F'}}>{r.gpo_no}</td>
                          <td style={{padding:'8px 10px'}}>{r.style||'-'}</td>
                          <td style={{padding:'8px 10px'}}>{r.buyer||'-'}</td>
                          <td style={{padding:'8px 10px',textAlign:'center',color:mhColor(r.cutting),fontWeight:600}}>{r.cutting ? r.cutting+'m' : '-'}</td>
                          <td style={{padding:'8px 10px',textAlign:'center',color:mhColor(r.stitching),fontWeight:600}}>{r.stitching ? r.stitching+'m' : '-'}</td>
                          <td style={{padding:'8px 10px',textAlign:'center',color:mhColor(r.finishing),fontWeight:600}}>{r.finishing ? r.finishing+'m' : '-'}</td>
                          <td style={{padding:'8px 10px',textAlign:'center'}}>
                            <span style={{padding:'4px 12px',borderRadius:10,fontWeight:700,fontSize:13,color:mhColor(r.total_mh)}}>{r.total_mh}m</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {tab==='office_mh' && (
              <div>
                <div style={{marginBottom:12,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span style={{fontSize:13,color:'#666'}}>Office staff manhour tracking - all departments</span>
                  <button onClick={()=>setShowOfficeModal(true)} style={{padding:'6px 14px',background:'#3B3A8F',color:'white',border:'none',borderRadius:6,fontSize:13,cursor:'pointer',fontWeight:600}}>+ Log Attendance</button>
                </div>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                  <thead><tr style={{background:'#F7F6F3'}}>
                    {['Date','Department','Staff','Start','End','OT Mins','Net Mins/Person','Total Dept Hours','Remarks'].map(h=>(
                      <th key={h} style={{padding:'8px 10px',textAlign:'left',color:'#555',fontWeight:600,borderBottom:'2px solid #eee',fontSize:11}}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {officeAttendance.length===0 ? (
                      <tr><td colSpan={9} style={{padding:40,textAlign:'center',color:'#aaa'}}>No office attendance logged yet - click Log Attendance to start</td></tr>
                    ) : officeAttendance.map((r,i)=>{
                      const netMins = Math.round(Number(r.net_mins)||0);
                      const totalHrs = Math.round(netMins * r.staff_count / 60);
                      return (
                        <tr key={i} style={{borderBottom:'1px solid #f5f5f5'}}>
                          <td style={{padding:'8px 10px'}}>{fmtDate(r.log_date)}</td>
                          <td style={{padding:'8px 10px',fontWeight:600}}>{r.dept}</td>
                          <td style={{padding:'8px 10px',textAlign:'center',fontWeight:600,color:'#3B3A8F'}}>{r.staff_count}</td>
                          <td style={{padding:'8px 10px'}}>{r.start_time}</td>
                          <td style={{padding:'8px 10px'}}>{r.end_time}</td>
                          <td style={{padding:'8px 10px',textAlign:'center',color:'#E22D2D'}}>{r.overtime_mins||0}</td>
                          <td style={{padding:'8px 10px',textAlign:'center',fontWeight:600}}>{netMins}</td>
                          <td style={{padding:'8px 10px',textAlign:'center',fontWeight:700,color:'#3B3A8F'}}>{totalHrs} hrs</td>
                          <td style={{padding:'8px 10px',color:'#888'}}>{r.remarks||'-'}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <ListFooter shown={officeAttendance.length} total={officeAttendance.length} noun="attendance records" />
              </div>
            )}

            {tab==='monthly' && (
              <div>
                <div style={{marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span style={{fontWeight:700,fontSize:15,color:'#3B3A8F'}}>{MONTHS_LIST[month-1]} {year} - Monthly Production Plan</span>
                  <div style={{display:'flex',gap:8}}>
                    <select value={month} onChange={e=>setMonth(Number(e.target.value))} style={{padding:'5px 10px',border:'1px solid #ddd',borderRadius:5,fontSize:12}}>
                      {MONTHS_LIST.map((m,i)=><option key={i} value={i+1}>{m}</option>)}
                    </select>
                    <select value={year} onChange={e=>setYear(Number(e.target.value))} style={{padding:'5px 10px',border:'1px solid #ddd',borderRadius:5,fontSize:12}}>
                      {[2024,2025,2026].map(y=><option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </div>
                {(monthly.weeks||[]).length>0 && (
                  <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fit,minmax(150px,1fr))',gap:12,marginBottom:20}}>
                    {(monthly.weeks||[]).map((w,i)=>(
                      <div key={i} style={{background:'white',border:'1px solid #eee',borderRadius:8,padding:14}}>
                        <div style={{fontSize:11,color:'#888',marginBottom:4}}>Week {i+1} - {fmtDate(w.week_start)}</div>
                        <div style={{fontSize:13,fontWeight:700,color:'#3B3A8F'}}>{Number(w.week_target||0).toLocaleString('en-IN')} plan</div>
                        <div style={{fontSize:13,color:'#166534',fontWeight:700}}>{Number(w.week_actual||0).toLocaleString('en-IN')} actual</div>
                        <div style={{fontSize:12,color:effColor(w.efficiency),fontWeight:700}}>{w.efficiency||0}%</div>
                      </div>
                    ))}
                  </div>
                )}
                {(monthly.cutting||[]).length===0 ? (
                  <div style={{padding:40,textAlign:'center',color:'#aaa',border:'1px dashed #eee',borderRadius:8}}>No cutting data for {MONTHS_LIST[month-1]} {year}</div>
                ) : (
                  <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                    <thead><tr style={{background:'#F7F6F3'}}>
                      {['GPO','Style','Buyer','Order Qty','Month Target','Month Actual','Balance','Efficiency','Period'].map(h=>(
                        <th key={h} style={{padding:'8px 10px',textAlign:'left',color:'#555',fontWeight:600,borderBottom:'2px solid #eee',fontSize:11}}>{h}</th>
                      ))}
                    </tr></thead>
                    <tbody>
                      {(monthly.cutting||[]).map((r,i)=>{
                        const bal = (r.order_cut_qty||0)-(r.month_actual||0);
                        return (
                          <tr key={i} style={{borderBottom:'1px solid #f5f5f5'}}>
                            <td style={{padding:'8px 10px',fontFamily:'monospace',fontWeight:600,color:'#3B3A8F'}}>{r.gpo_no}</td>
                            <td style={{padding:'8px 10px'}}>{r.style}</td>
                            <td style={{padding:'8px 10px'}}>{r.buyer}</td>
                            <td style={{padding:'8px 10px',textAlign:'right'}}>{Number(r.order_cut_qty||0).toLocaleString('en-IN')}</td>
                            <td style={{padding:'8px 10px',textAlign:'right',color:'#3B3A8F',fontWeight:600}}>{Number(r.month_target||0).toLocaleString('en-IN')}</td>
                            <td style={{padding:'8px 10px',textAlign:'right',color:'#166534',fontWeight:600}}>{Number(r.month_actual||0).toLocaleString('en-IN')}</td>
                            <td style={{padding:'8px 10px',textAlign:'right',color:bal>0?'#ef4444':'#166534',fontWeight:600}}>{Number(bal).toLocaleString('en-IN')}</td>
                            <td style={{padding:'8px 10px',textAlign:'center'}}>
                              <span style={{padding:'2px 8px',borderRadius:10,fontSize:11,fontWeight:600,color:effColor(r.efficiency)}}>{r.efficiency||0}%</span>
                            </td>
                            <td style={{padding:'8px 10px',fontSize:11,color:'#888'}}>{fmtDate(r.start_date)} - {fmtDate(r.end_date)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                )}
              </div>
            )}

            {tab==='quarterly' && (
              <div>
                <div style={{marginBottom:16,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                  <span style={{fontWeight:700,fontSize:15,color:'#3B3A8F'}}>Q{quarter} {year} - Quarterly Plan</span>
                  <div style={{display:'flex',gap:8}}>
                    <select value={quarter} onChange={e=>setQuarter(Number(e.target.value))} style={{padding:'5px 10px',border:'1px solid #ddd',borderRadius:5,fontSize:12}}>
                      <option value={1}>Q1 Jan-Mar</option><option value={2}>Q2 Apr-Jun</option>
                      <option value={3}>Q3 Jul-Sep</option><option value={4}>Q4 Oct-Dec</option>
                    </select>
                    <select value={year} onChange={e=>setYear(Number(e.target.value))} style={{padding:'5px 10px',border:'1px solid #ddd',borderRadius:5,fontSize:12}}>
                      {[2024,2025,2026].map(y=><option key={y} value={y}>{y}</option>)}
                    </select>
                  </div>
                </div>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
                  <div>
                    <div style={{fontWeight:700,fontSize:13,marginBottom:10}}>Buyer-wise Order Loading</div>
                    {(quarterly.buyer_wise||[]).length===0 ? (
                      <div style={{padding:20,textAlign:'center',color:'#aaa',border:'1px dashed #eee',borderRadius:8}}>No orders for Q{quarter} {year}</div>
                    ) : (
                      <table style={{width:'100%',borderCollapse:'collapse',fontSize:12}}>
                        <thead><tr style={{background:'#F7F6F3'}}>
                          {['Buyer','Month','Orders','Total Qty'].map(h=>(
                            <th key={h} style={{padding:'7px 10px',textAlign:'left',color:'#555',fontWeight:600,borderBottom:'2px solid #eee',fontSize:11}}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {(quarterly.buyer_wise||[]).map((r,i)=>(
                            <tr key={i} style={{borderBottom:'1px solid #f5f5f5'}}>
                              <td style={{padding:'7px 10px',fontWeight:600}}>{r.buyer_name}</td>
                              <td style={{padding:'7px 10px'}}>{['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'][(r.month||1)-1]}</td>
                              <td style={{padding:'7px 10px',textAlign:'center'}}>{r.order_count}</td>
                              <td style={{padding:'7px 10px',textAlign:'right',fontWeight:600,color:'#3B3A8F'}}>{Number(r.total_qty||0).toLocaleString('en-IN')}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    )}
                  </div>
                  <div>
                    <div style={{fontWeight:700,fontSize:13,marginBottom:10}}>Capacity vs Loading (Weekly)</div>
                    {(quarterly.capacity_wise||[]).length===0 ? (
                      <div style={{padding:20,textAlign:'center',color:'#aaa',border:'1px dashed #eee',borderRadius:8}}>No capacity data for Q{quarter} {year}</div>
                    ) : (quarterly.capacity_wise||[]).map((w,i)=>{
                      const pct = w.planned_qty>0 ? Math.min(100,Math.round(w.actual_qty/w.planned_qty*100)) : 0;
                      return (
                        <div key={i} style={{marginBottom:10,padding:'10px 14px',background:'#F7F6F3',borderRadius:6}}>
                          <div style={{display:'flex',justifyContent:'space-between',marginBottom:4}}>
                            <span style={{fontSize:12,fontWeight:600}}>{fmtDate(w.week_start)}</span>
                            <span style={{fontSize:12,fontWeight:700,color:effColor(w.efficiency)}}>{w.efficiency||0}%</span>
                          </div>
                          <div style={{display:'flex',justifyContent:'space-between',fontSize:11,marginBottom:6}}>
                            <span style={{color:'#3B3A8F'}}>Plan: {Number(w.planned_qty||0).toLocaleString('en-IN')}</span>
                            <span style={{color:'#166534'}}>Actual: {Number(w.actual_qty||0).toLocaleString('en-IN')}</span>
                          </div>
                          <div style={{height:8,background:'#e5e7eb',borderRadius:4}}>
                            <div style={{height:'100%',width:pct+'%',background:effColor(w.efficiency),borderRadius:4}}/>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            )}

      {showOfficeModal && (
        <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.45)',display:'flex',alignItems:'center',justifyContent:'center',zIndex:1000}}>
          <div style={{background:'white',borderRadius:12,padding:28,width:440}}>
            <div style={{ background: '#2D2C72', margin: '-28px -28px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>Log Office Attendance</h3></div>
            <form onSubmit={async(e)=>{
              e.preventDefault();setSaving(true);
              try{
                await api.post('/planning/office-attendance',{...officeForm,log_date:localDate()});
                setShowOfficeModal(false);
                api.get('/planning/office-attendance').then(r=>setOfficeAttendance(r.data.data||[]));
              }catch(err){alert('Error: '+err.message);}
              finally{setSaving(false);}
            }}>
              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>
                <div style={{gridColumn:'1/-1'}}>
                  <label style={labelStyle}>Department</label>
                  <select style={inputStyle} value={officeForm.dept} onChange={e=>setOfficeForm(p=>({...p,dept:e.target.value}))}>
                    {['Merchandising','Procurement','Production','PPC','Quality','Accounts','Stores','HR','Management'].map(d=><option key={d} value={d}>{d}</option>)}
                  </select>
                </div>
                <div><label style={labelStyle}>Staff Count</label><input style={inputStyle} type="number" required value={officeForm.staff_count} onChange={e=>setOfficeForm(p=>({...p,staff_count:e.target.value}))}/></div>
                <div><label style={labelStyle}>OT Mins</label><input style={inputStyle} type="number" value={officeForm.overtime_mins} onChange={e=>setOfficeForm(p=>({...p,overtime_mins:e.target.value}))}/></div>
                <div style={{gridColumn:'1/-1'}}><label style={labelStyle}>Remarks</label><input style={inputStyle} onChange={e=>setOfficeForm(p=>({...p,remarks:e.target.value}))}/></div>
              </div>
              <div style={{display:'flex',gap:10,marginTop:20,justifyContent:'flex-end'}}>
                <button type="button" onClick={()=>setShowOfficeModal(false)} style={{padding:'8px 18px',background:'#f0f2f8',border:'1px solid #ddd',borderRadius:6,fontSize:13,cursor:'pointer'}}>Cancel</button>
                <button type="submit" disabled={saving} style={{padding:'8px 18px',background:'#3B3A8F',color:'white',border:'none',borderRadius:6,fontSize:13,cursor:'pointer',fontWeight:600}}>{saving?'Saving...':'Log'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
      {showPlanModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'white', borderRadius:12, padding:28, width:480, maxHeight:'90vh', overflowY:'auto' }}>
            <div style={{ background: '#2D2C72', margin: '-28px -28px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>Add {tab.charAt(0).toUpperCase()+tab.slice(1)} Plan — Line {planForm.line_no} · {fmtDate(planForm.plan_date)}{planForm.sub_dept?' · '+DEPT_LABELS[planForm.sub_dept]:''}</h3></div>
            <form onSubmit={savePlan}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div style={{ gridColumn:'1/-1' }}>
                  <label style={labelStyle}>Select GPO / Order *</label>
                  <select style={inputStyle} required onChange={e => { const o=orders.find(x=>x.id===e.target.value); if(o) { setPlanForm(p=>({...p,order_id:o.id,gpo_no:o.gpo_no||o.order_no,style:o.style||o.garment_type||'',buyer:o.buyer_name||''})); api.get('/planning/tna-dates/'+o.id).then(r=>{const d={};(r.data.data||[]).forEach(x=>{d[x.activity_code]=x;});setTatDates(d);}).catch(()=>{}); }}}>"  
                    <option value="">Select order</option>
                    {orders.map(o => <option key={o.id} value={o.id}>{o.gpo_no||o.order_no} — {o.buyer_name} — {o.style||o.garment_type}</option>)}
                  </select>
                </div>
                {tab==='cutting' && <div style={{ gridColumn:'1/-1' }}><label style={labelStyle}>Fabric Type</label><input style={inputStyle} placeholder="e.g. 30x30 Twill" onChange={e => setPlanForm(p=>({...p,fabric_type:e.target.value}))} /></div>}
                {planForm.order_id && Object.keys(tatDates).length > 0 && (
                  <div style={{ gridColumn:'1/-1', padding:'12px 14px', borderRadius:6,
                    background: tatDates['ST5_CUTTING'] && new Date(planForm.plan_date) > new Date(tatDates['ST5_CUTTING'].planned_date) ? '#fee2e2' : '#dcfce7',
                    border: '1px solid ' + (tatDates['ST5_CUTTING'] && new Date(planForm.plan_date) > new Date(tatDates['ST5_CUTTING'].planned_date) ? '#fca5a5' : '#86efac') }}>
                    <div style={{ fontSize:12, fontWeight:700, marginBottom:8 }}>
                      {tatDates['ST5_CUTTING'] && new Date(planForm.plan_date) > new Date(tatDates['ST5_CUTTING'].planned_date) ? '🔴 Planning LATE vs T&A' : '🟢 ON TRACK vs T&A'}
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:6, fontSize:11 }}>
                      {tatDates['ST5_CUTTING'] && <span>✂️ T&A Cutting: <b>{new Date(tatDates['ST5_CUTTING'].planned_date).toLocaleDateString('en-IN')}</b></span>}
                      {tatDates['ST5_PALLA_BANK'] && <span>📦 T&A Palla Bank: <b>{new Date(tatDates['ST5_PALLA_BANK'].planned_date).toLocaleDateString('en-IN')}</b></span>}
                      {tatDates['ST7_LINE_IN'] && <span>🪡 T&A Line In: <b>{new Date(tatDates['ST7_LINE_IN'].planned_date).toLocaleDateString('en-IN')}</b></span>}
                      {tatDates['ST7_LINE_OUT'] && <span>👕 T&A Line Out: <b>{new Date(tatDates['ST7_LINE_OUT'].planned_date).toLocaleDateString('en-IN')}</b></span>}
                      {tatDates['ST8_PACKING'] && <span>📦 T&A Packing: <b>{new Date(tatDates['ST8_PACKING'].planned_date).toLocaleDateString('en-IN')}</b></span>}
                      {tatDates['ST9_DISPATCH'] && <span>🚚 T&A Dispatch: <b>{new Date(tatDates['ST9_DISPATCH'].planned_date).toLocaleDateString('en-IN')}</b></span>}
                    </div>
                  </div>
                )}
                <div><label style={labelStyle}>Order Qty</label><input style={inputStyle} type="number" onChange={e => setPlanForm(p=>({...p,order_cut_qty:e.target.value,order_qty:e.target.value}))} /></div>
                <div><label style={labelStyle}>Target Qty *</label><input style={inputStyle} type="number" required onChange={e => setPlanForm(p=>({...p,target_qty:e.target.value}))} /></div>
                {tab==='stitching' && <div><label style={labelStyle}>SAM (optional)</label><input style={inputStyle} type="number" step="0.01" placeholder="e.g. 18.50" onChange={e => setPlanForm(p=>({...p,sam:e.target.value}))} /></div>}
              </div>
              <div style={{ display:'flex', gap:10, marginTop:20, justifyContent:'flex-end' }}>
                <button type="button" onClick={() => setShowPlanModal(false)} style={{ padding:'8px 18px', background:'#f0f2f8', border:'1px solid #ddd', borderRadius:6, fontSize:13, cursor:'pointer' }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ padding:'8px 18px', background:'#3B3A8F', color:'white', border:'none', borderRadius:6, fontSize:13, cursor:'pointer', fontWeight:600 }}>{saving?'Saving...':'Save Plan'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {showActualModal && selectedCell && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.45)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'white', borderRadius:12, padding:28, width:420 }}>
            <div style={{ background: '#2D2C72', margin: '-28px -28px 18px', padding: '14px 24px', borderRadius: '12px 12px 0 0' }}><h3 style={{ margin: 0, fontSize: 16, color: '#fff', fontWeight: 700 }}>Update Actual</h3></div>
            <p style={{ margin:'0 0 16px', fontSize:12, color:'#888' }}>{selectedCell.gpo_no} · Line {selectedCell.line_no} · {fmtDate(selectedCell.plan_date)}</p>
            <div style={{ background:'#EEEDF8', borderRadius:6, padding:'8px 12px', marginBottom:16, fontSize:13 }}>Target: <b>{Number(selectedCell.target_qty||0).toLocaleString('en-IN')} pcs</b></div>
            <form onSubmit={saveActual}>
              <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:12 }}>
                <div><label style={labelStyle}>Made (total) *</label><input style={inputStyle} type="number" required value={actualForm.actual_qty} onChange={e => setActualForm(p=>({...p,actual_qty:e.target.value}))} /></div>
                <div><label style={labelStyle}>Rejected</label><input style={inputStyle} type="number" value={actualForm.rejected_qty} onChange={e => setActualForm(p=>({...p,rejected_qty:e.target.value}))} /></div>
                <div><label style={labelStyle}>Reworked</label><input style={inputStyle} type="number" value={actualForm.rework_qty} onChange={e => setActualForm(p=>({...p,rework_qty:e.target.value}))} /></div>
                <div><label style={labelStyle}>Operators</label><input style={inputStyle} type="number" value={actualForm.operator_count} onChange={e => setActualForm(p=>({...p,operator_count:e.target.value}))} /></div>
                {tab==='stitching' && <div><label style={labelStyle}>Helpers</label><input style={inputStyle} type="number" value={actualForm.helper_count} onChange={e => setActualForm(p=>({...p,helper_count:e.target.value}))} /></div>}
                <div><label style={labelStyle}>OT Mins</label><input style={inputStyle} type="number" value={actualForm.overtime_mins} onChange={e => setActualForm(p=>({...p,overtime_mins:e.target.value}))} /></div>
                {tab==='stitching' && <div><label style={labelStyle}>SAM</label><input style={inputStyle} type="number" step="0.01" value={actualForm.sam} onChange={e => setActualForm(p=>({...p,sam:e.target.value}))} /></div>}
                <div style={{ gridColumn:'1/-1' }}><label style={labelStyle}>Remarks</label><input style={inputStyle} value={actualForm.remarks} onChange={e => setActualForm(p=>({...p,remarks:e.target.value}))} /></div>
              </div>
              {Number(actualForm.actual_qty)>0 && (
                <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:6, padding:'8px 12px', marginTop:10, fontSize:12, display:'flex', gap:20 }}>
                  <span>Net good: <b style={{ color:'#166534' }}>{(Number(actualForm.actual_qty)-(Number(actualForm.rejected_qty)||0)).toLocaleString('en-IN')}</b> pcs</span>
                  <span>Yield: <b style={{ color:(1-(Number(actualForm.rejected_qty)||0)/Number(actualForm.actual_qty))<0.95?'#B45309':'#166534' }}>{Math.round((1-(Number(actualForm.rejected_qty)||0)/Number(actualForm.actual_qty))*1000)/10}%</b></span>
                </div>
              )}
              {Number(actualForm.actual_qty)>0 && Number(actualForm.operator_count)>0 && (
                <div style={{ background:'#F7F6F3', borderRadius:6, padding:'10px 12px', marginTop:12, fontSize:12 }}>
                  <b>Live Calculation:</b>
                  <div style={{ display:'flex', gap:20, marginTop:6 }}>
                    <span>Efficiency: <b style={{ color:effColor(calcEff(actualForm.actual_qty,selectedCell.target_qty)) }}>{calcEff(actualForm.actual_qty,selectedCell.target_qty)||'—'}%</b></span>
                    <span>MH/Garment: <b style={{ color:mhColor(calcMH(actualForm.operator_count,actualForm.helper_count,actualForm.actual_qty,actualForm.overtime_mins)) }}>{calcMH(actualForm.operator_count,actualForm.helper_count,actualForm.actual_qty,actualForm.overtime_mins)||'—'} mins</b></span>
                  </div>
                </div>
              )}
              <div style={{ display:'flex', gap:10, marginTop:16, justifyContent:'flex-end' }}>
                <button type="button" onClick={() => setShowActualModal(false)} style={{ padding:'8px 18px', background:'#f0f2f8', border:'1px solid #ddd', borderRadius:6, fontSize:13, cursor:'pointer' }}>Cancel</button>
                <button type="submit" disabled={saving} style={{ padding:'8px 18px', background:'#E22D2D', color:'white', border:'none', borderRadius:6, fontSize:13, cursor:'pointer', fontWeight:600 }}>{saving?'Saving...':'Save Actual'}</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </>
  );
}

export default function PPC() {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  return (
    <div style={{ minHeight:'100vh', background:'#f0f2f8', fontFamily:'Arial, sans-serif' }}>
      
      <PPCContent />
    </div>
  );
}
